import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  RefreshCw,
  Save,
  X
} from 'lucide-react';
import costEstimationService from '../../services/costEstimationService';

const BarStockPriceManager = ({ open, onClose }) => {
  const [barStocks, setBarStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [isCreating, setIsCreating] = useState(false);
  const [newForm, setNewForm] = useState({
    diameter: '',
    length: 310,
    unit_price_tax_included: '',
    tax_rate: 0.13,
    supplier_name: '厦门金鹭',
    brand: 'GU20',
    delivery_days: 15,
    notes: ''
  });

  const fetchBarStocks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await costEstimationService.getBarStockPrices({
        pageSize: 100,
        isActive: true
      });
      setBarStocks(result.items || []);
    } catch (err) {
      setError(err.response?.data?.error || '加载棒料价格失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchBarStocks();
    }
  }, [open, fetchBarStocks]);

  const handleClose = () => {
    setEditingId(null);
    setIsCreating(false);
    setError(null);
    onClose();
  };

  const handleEdit = (barStock) => {
    setEditingId(barStock._id);
    setEditForm({
      unit_price_tax_included: barStock.unit_price_tax_included,
      supplier_name: barStock.supplier_name || '',
      brand: barStock.brand || '',
      delivery_days: barStock.delivery_days || '',
      notes: barStock.notes || ''
    });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const handleSaveEdit = async (id) => {
    setLoading(true);
    try {
      await costEstimationService.updateBarStockPrice(id, editForm);
      setEditingId(null);
      setEditForm({});
      await fetchBarStocks();
    } catch (err) {
      setError(err.response?.data?.error || '更新失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个棒料价格吗？')) return;

    setLoading(true);
    try {
      await costEstimationService.deleteBarStockPrice(id);
      await fetchBarStocks();
    } catch (err) {
      setError(err.response?.data?.error || '删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newForm.diameter || !newForm.unit_price_tax_included) {
      setError('直径和含税单价是必填项');
      return;
    }

    setLoading(true);
    try {
      await costEstimationService.createBarStockPrice({
        ...newForm,
        diameter: parseFloat(newForm.diameter),
        unit_price_tax_included: parseFloat(newForm.unit_price_tax_included),
        delivery_days: newForm.delivery_days ? parseInt(newForm.delivery_days) : null
      });
      setIsCreating(false);
      setNewForm({
        diameter: '',
        length: 310,
        unit_price_tax_included: '',
        tax_rate: 0.13,
        supplier_name: '厦门金鹭',
        brand: 'GU20',
        delivery_days: 15,
        notes: ''
      });
      await fetchBarStocks();
    } catch (err) {
      setError(err.response?.data?.error || '创建失败');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return `¥${parseFloat(value).toFixed(2)}`;
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            棒料价格管理
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchBarStocks}
              disabled={loading}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Create New Form */}
          {isCreating && (
            <div className="mb-4 p-4 border rounded-lg bg-muted/50">
              <h4 className="font-medium mb-3">添加新棒料价格</h4>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label htmlFor="new-diameter">直径 (mm)</Label>
                  <Input
                    id="new-diameter"
                    type="number"
                    step="0.5"
                    value={newForm.diameter}
                    onChange={(e) => setNewForm({ ...newForm, diameter: e.target.value })}
                    placeholder="例: 3.5"
                  />
                </div>
                <div>
                  <Label htmlFor="new-length">长度 (mm)</Label>
                  <Input
                    id="new-length"
                    type="number"
                    value={newForm.length}
                    onChange={(e) => setNewForm({ ...newForm, length: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label htmlFor="new-price">含税单价 (元)</Label>
                  <Input
                    id="new-price"
                    type="number"
                    step="0.01"
                    value={newForm.unit_price_tax_included}
                    onChange={(e) => setNewForm({ ...newForm, unit_price_tax_included: e.target.value })}
                    placeholder="例: 96.06"
                  />
                </div>
                <div>
                  <Label htmlFor="new-supplier">供应商</Label>
                  <Input
                    id="new-supplier"
                    value={newForm.supplier_name}
                    onChange={(e) => setNewForm({ ...newForm, supplier_name: e.target.value })}
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button size="sm" onClick={handleCreate} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  保存
                </Button>
                <Button size="sm" variant="outline" onClick={() => setIsCreating(false)}>
                  取消
                </Button>
              </div>
            </div>
          )}

          {/* Price Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-20">规格</TableHead>
                  <TableHead className="w-24 text-right">含税单价</TableHead>
                  <TableHead className="w-24 text-right">不含税单价</TableHead>
                  <TableHead>供应商</TableHead>
                  <TableHead className="w-16 text-center">牌号</TableHead>
                  <TableHead className="w-16 text-center">交期</TableHead>
                  <TableHead className="w-24 text-center">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && barStocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : barStocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      暂无棒料价格数据
                    </TableCell>
                  </TableRow>
                ) : (
                  barStocks.map((barStock) => (
                    <TableRow key={barStock._id}>
                      <TableCell className="font-medium">
                        {barStock.specification}
                      </TableCell>
                      <TableCell className="text-right">
                        {editingId === barStock._id ? (
                          <Input
                            type="number"
                            step="0.01"
                            value={editForm.unit_price_tax_included}
                            onChange={(e) => setEditForm({ ...editForm, unit_price_tax_included: parseFloat(e.target.value) })}
                            className="w-24 text-right"
                          />
                        ) : (
                          formatCurrency(barStock.unit_price_tax_included)
                        )}
                      </TableCell>
                      <TableCell className="text-right text-muted-foreground">
                        {formatCurrency(barStock.unit_price_tax_excluded)}
                      </TableCell>
                      <TableCell>
                        {editingId === barStock._id ? (
                          <Input
                            value={editForm.supplier_name}
                            onChange={(e) => setEditForm({ ...editForm, supplier_name: e.target.value })}
                            className="w-28"
                          />
                        ) : (
                          barStock.supplier_name || '-'
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {barStock.brand || '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        {barStock.delivery_days ? `${barStock.delivery_days}天` : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center gap-1">
                          {editingId === barStock._id ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSaveEdit(barStock._id)}
                                disabled={loading}
                              >
                                <Save className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelEdit}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(barStock)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(barStock._id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button
            variant="outline"
            onClick={() => setIsCreating(true)}
            disabled={isCreating}
          >
            <Plus className="h-4 w-4 mr-2" />
            添加价格
          </Button>
          <Button variant="secondary" onClick={handleClose}>
            关闭
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BarStockPriceManager;
